import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  Upload,
  FileText,
  Tag,
  CalendarDays,
  Clock3,
  Plus,
  Trash2,
  Eye,
  Download,
  ChevronDown,
  Info,
  Pencil,
  X,
  History,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";

import { useTranslation } from "react-i18next";

import { useAuth0 } from "@auth0/auth0-react";

import { useOutletContext } from "react-router-dom";

import "./HomebuyerInvoicesPage.css";

import {
  uploadRenovationInvoice,
  deleteRenovationInvoice,
  replaceRenovationInvoice,
  listRenovationInvoices,
  fetchRenovationInvoiceFile,
} from "./invoiceApi";

const RENOVATION_TYPE_KEYS = [
  "facadeInsulation",
  "roofInsulation",
  "windowReplacement",
  "heatPumpInstallation",
  "solarPanels",
  "ventilationSystem",
  "heatingSystemUpgrade",
  "floorInsulation",
  "electricalWorks",
  "other",
];

/* =========================================================
   AMOUNT HELPERS
   ========================================================= */

function sanitizeAmountInput(rawValue) {
  let cleaned = rawValue.replace(/[^\d.]/g, "");

  const firstDotIndex = cleaned.indexOf(".");

  if (firstDotIndex !== -1) {
    const integerPart = cleaned.slice(0, firstDotIndex);

    const decimalPart = cleaned
      .slice(firstDotIndex + 1)
      .replace(/\./g, "")
      .slice(0, 2);

    cleaned = `${integerPart}.${decimalPart}`;
  }

  if (cleaned.startsWith(".")) {
    cleaned = `0${cleaned}`;
  }

  return cleaned;
}

function formatCurrencyDisplay(value) {
  if (!value) {
    return "";
  }

  const hasTrailingDot = value.endsWith(".");

  const [integerPartRaw, decimalPart] = value.split(".");

  const integerPartNumber = Number(integerPartRaw || "0");

  const formattedIntegerPart = integerPartNumber.toLocaleString("en-GB");

  if (hasTrailingDot) {
    return `${formattedIntegerPart}.`;
  }

  if (decimalPart !== undefined) {
    return `${formattedIntegerPart}.` + decimalPart;
  }

  return formattedIntegerPart;
}

function formatEuro(value) {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  const amount = Number(value);

  if (!Number.isFinite(amount)) {
    return "—";
  }

  return `${amount.toLocaleString("en-GB", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} €`;
}

/* =========================================================
   VERSION
   ========================================================= */

function formatVersionLabel(version, t) {
  if (version === 0 || version === 1 || version === "1") {
    return t("versionLabels.initial", "Initial");
  }

  const numericVersion = Number(version);

  if (!Number.isNaN(numericVersion) && numericVersion > 1) {
    return t("versionLabels.revision", {
      defaultValue: "Revision {{number}}",

      number: numericVersion - 1,
    });
  }

  return version || "—";
}

/* =========================================================
   COMPONENT
   ========================================================= */

export default function HomebuyerInvoicesPage() {
  const { t } = useTranslation("homebuyer-invoices");

  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const { dashboardSummary } = useOutletContext();

  /* =======================================================
     PROJECT
     ======================================================= */
  const loanCaseId = dashboardSummary?.loan_case_id || null;

  const projectId =
    dashboardSummary?.project_id ||
    dashboardSummary?.renovation_project_id ||
    null;

  const hasInvoiceContext = Boolean(loanCaseId || projectId);

  /* =======================================================
     STATE
     ======================================================= */

  const [backendInvoices, setBackendInvoices] = useState([]);

  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);

  const [projectSummary, setProjectSummary] = useState(null);

  /* =======================================================
     UPLOAD FORM
     ======================================================= */

  const fileInputRef = useRef(null);

  const replaceModalFileInputRef = useRef(null);

  const alertTimeoutRef = useRef(null);

  const [selectedFile, setSelectedFile] = useState(null);

  const [renovationType, setRenovationType] = useState("");

  const [amountInput, setAmountInput] = useState("");

  const [notes, setNotes] = useState("");

  const [dragActive, setDragActive] = useState(false);

  const [isUploading, setIsUploading] = useState(false);

  /* =======================================================
     ALERT
     ======================================================= */

  const [alertState, setAlertState] = useState({
    type: "",
    message: "",
    visible: false,
  });

  /* =======================================================
     PREVIEW
     ======================================================= */

  const [previewInvoice, setPreviewInvoice] = useState(null);

  const [previewUrl, setPreviewUrl] = useState("");

  /* =======================================================
     EDIT / REPLACE
     ======================================================= */

  const [editInvoice, setEditInvoice] = useState(null);

  const [editRenovationType, setEditRenovationType] = useState("");

  const [editAmountInput, setEditAmountInput] = useState("");

  const [editNotes, setEditNotes] = useState("");

  const [editSelectedFile, setEditSelectedFile] = useState(null);

  const [isSavingEdit, setIsSavingEdit] = useState(false);

  /* =======================================================
     DELETE MODAL
     ======================================================= */

  const [deleteInvoice, setDeleteInvoice] = useState(null);

  const [isDeletingInvoice, setIsDeletingInvoice] = useState(false);

  /* =======================================================
     PROJECT META
     ======================================================= */

  const operationCode =
    projectSummary?.code ||
    dashboardSummary?.project_code ||
    dashboardSummary?.renovation_project_code ||
    dashboardSummary?.loan_case_reference ||
    dashboardSummary?.dossier_code ||
    (loanCaseId
      ? `Loan case #${loanCaseId}`
      : projectId
        ? `Project #${projectId}`
        : "—");

  const lastUpdateRaw =
    projectSummary?.updated_at ||
    dashboardSummary?.project_last_update ||
    dashboardSummary?.renovation_last_update ||
    dashboardSummary?.last_update ||
    null;

  const lastUpdateLabel = lastUpdateRaw
    ? new Date(lastUpdateRaw).toLocaleDateString("en-GB")
    : "—";

  /* =======================================================
     LOAD INVOICES
     ======================================================= */

  const refreshInvoices = async () => {
    if (!hasInvoiceContext || !isAuthenticated) {
      setBackendInvoices([]);
      setProjectSummary(null);
      return;
    }

    try {
      setIsLoadingInvoices(true);

      const accessToken = await getAccessTokenSilently();

      const data = await listRenovationInvoices({
        accessToken,
        loanCaseId,
        projectId,
      });

      const results = Array.isArray(data) ? data : data?.results || [];

      setBackendInvoices(results);

      if (!Array.isArray(data)) {
        if (data?.project_summary) {
          // Project endpoint response.
          setProjectSummary(data.project_summary);
        } else if (data?.project) {
          // Loan-case endpoint where an internal
          // RenovationProject exists.
          setProjectSummary(data.project);
        } else {
          // External OSS / projectless renovation.
          setProjectSummary(null);
        }
      }
    } catch (error) {
      showAlert(
        "error",
        error.message || t("alerts.loadFailed", "Could not load invoices."),
      );
    } finally {
      setIsLoadingInvoices(false);
    }
  };

  useEffect(() => {
    refreshInvoices();
  }, [loanCaseId, projectId, isAuthenticated]);

  const effectiveInvoices = backendInvoices || [];

  /* =======================================================
     TOTAL INVOICED
     ======================================================= */

  const totalAmount = useMemo(() => {
    return effectiveInvoices.reduce((sum, invoice) => {
      const amount = invoice.latest_amount ?? invoice.amount ?? 0;

      return sum + Number(amount || 0);
    }, 0);
  }, [effectiveInvoices]);

  /* =======================================================
   INITIAL RENOVATION BUDGET
   ======================================================= */

  const initialRenovationEstimate = Number(
    projectSummary?.estimated_renovation_budget ??
      dashboardSummary?.estimated_renovation_budget ??
      0,
  );

  const safeInitialRenovationEstimate = Number.isFinite(
    initialRenovationEstimate,
  )
    ? initialRenovationEstimate
    : 0;

  /* =======================================================
   LENDER RENOVATION FINANCING
   ======================================================= */
  const lenderDecision = String(
    projectSummary?.loan_decision ?? dashboardSummary?.loan_decision ?? "",
  ).toUpperCase();

  const renovationFinancingStatus = String(
    projectSummary?.renovation_financing_status ??
      dashboardSummary?.renovation_financing_status ??
      "",
  ).toUpperCase();

  const hasConfirmedLenderRenovationFinancing =
    lenderDecision === "APPROVED" && renovationFinancingStatus === "CONFIRMED";

  const lenderRenovationFinancing = Number(
    hasConfirmedLenderRenovationFinancing
      ? (projectSummary?.approved_renovation_loan_amount ??
          dashboardSummary?.approved_renovation_loan_amount ??
          0)
      : 0,
  );

  const safeLenderRenovationFinancing = Number.isFinite(
    lenderRenovationFinancing,
  )
    ? lenderRenovationFinancing
    : 0;

  /* =======================================================
   SUBSIDY / GRANT
   ======================================================= */

  /* =======================================================
   FUNDING SOURCES
   ======================================================= */

  const fundingSources = Array.isArray(dashboardSummary?.funding_sources)
    ? dashboardSummary.funding_sources
    : [];

  /* =======================================================
   NORMALISED ADDITIONAL FUNDING SOURCES
   ======================================================= */

  const additionalFundingSources = useMemo(() => {
    return fundingSources
      .map((source) => {
        const sourceType = String(
          source?.source_type || source?.funding_type || source?.type || "",
        ).toUpperCase();

        const status = String(source?.status || "EXPECTED").toUpperCase();

        const amount = Number(
          source?.amount ??
            source?.approved_amount ??
            source?.requested_amount ??
            0,
        );

        return {
          ...source,

          sourceType,

          status,

          amount: Number.isFinite(amount) ? amount : 0,

          name:
            source?.source_name ||
            source?.name ||
            source?.label ||
            source?.provider_name ||
            "",
        };
      })
      .filter((source) => source.amount > 0);
  }, [fundingSources]);

  /* =======================================================
   FUNDING TYPE TOTALS
   ======================================================= */

  const softLoanFundingTotal = additionalFundingSources
    .filter((source) => source.sourceType === "SOFT_LOAN")
    .reduce((total, source) => total + source.amount, 0);

  const subsidyFundingTotal = additionalFundingSources
    .filter(
      (source) =>
        source.sourceType === "SUBSIDY" || source.sourceType === "GRANT",
    )
    .reduce((total, source) => total + source.amount, 0);

  const ownFundsTotal = additionalFundingSources
    .filter((source) => source.sourceType === "OWN_FUNDS")
    .reduce((total, source) => total + source.amount, 0);

  const otherFundingTotal = additionalFundingSources
    .filter(
      (source) =>
        !["SOFT_LOAN", "SUBSIDY", "GRANT", "OWN_FUNDS"].includes(
          source.sourceType,
        ),
    )
    .reduce((total, source) => total + source.amount, 0);

  /* =======================================================
   CONFIRMED / EXPECTED FUNDING
   ======================================================= */

  const confirmedFundingTotal =
    safeLenderRenovationFinancing +
    additionalFundingSources
      .filter((source) =>
        ["APPROVED", "CONFIRMED", "ACTIVE"].includes(source.status),
      )
      .reduce((total, source) => total + source.amount, 0);

  const expectedFundingTotal = additionalFundingSources
    .filter((source) => source.status === "EXPECTED")
    .reduce((total, source) => total + source.amount, 0);

  const potentialFundingTotal = confirmedFundingTotal + expectedFundingTotal;

  /* =======================================================
   CURRENT INVOICE DOCUMENTATION TARGET
   ======================================================= */

  const hasLenderFinancingStructure =
    safeLenderRenovationFinancing > 0 || additionalFundingSources.length > 0;

  const invoiceDocumentationTarget =
    hasLenderFinancingStructure && potentialFundingTotal > 0
      ? potentialFundingTotal
      : safeInitialRenovationEstimate;

  /* =======================================================
   FUNDING BALANCE
   ======================================================= */

  const potentialFundingBalance =
    safeInitialRenovationEstimate > 0
      ? potentialFundingTotal - safeInitialRenovationEstimate
      : null;

  /* =======================================================
   INVOICE DOCUMENTATION PROGRESS
   ======================================================= */

  const remainingExpenditureToDocument =
    invoiceDocumentationTarget > 0
      ? Math.max(invoiceDocumentationTarget - totalAmount, 0)
      : null;

  const rawInvoicedProgress =
    invoiceDocumentationTarget > 0
      ? (totalAmount / invoiceDocumentationTarget) * 100
      : 0;

  const invoicedProgress = Math.min(Math.max(rawInvoicedProgress, 0), 100);

  const invoicesAboveTarget =
    invoiceDocumentationTarget > 0
      ? Math.max(totalAmount - invoiceDocumentationTarget, 0)
      : 0;

  /* =======================================================
     CLEANUP
     ======================================================= */

  useEffect(() => {
    return () => {
      if (alertTimeoutRef.current) {
        clearTimeout(alertTimeoutRef.current);
      }

      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  /* =======================================================
     ALERT HELPERS
     ======================================================= */

  const showAlert = (type, message) => {
    if (alertTimeoutRef.current) {
      clearTimeout(alertTimeoutRef.current);
    }

    setAlertState({
      type,
      message,
      visible: true,
    });

    alertTimeoutRef.current = setTimeout(() => {
      setAlertState((previous) => ({
        ...previous,
        visible: false,
      }));
    }, 4000);
  };

  const clearAlert = () => {
    if (alertTimeoutRef.current) {
      clearTimeout(alertTimeoutRef.current);
    }

    setAlertState({
      type: "",
      message: "",
      visible: false,
    });
  };

  /* =======================================================
     FORM RESET
     ======================================================= */

  const resetForm = () => {
    setSelectedFile(null);

    setRenovationType("");

    setAmountInput("");

    setNotes("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const closeEditModal = () => {
    setEditInvoice(null);

    setEditRenovationType("");

    setEditAmountInput("");

    setEditNotes("");

    setEditSelectedFile(null);

    if (replaceModalFileInputRef.current) {
      replaceModalFileInputRef.current.value = "";
    }
  };

  /* =======================================================
     FILE VALIDATION
     ======================================================= */

  const isPdfFile = (file) => {
    if (!file) {
      return false;
    }

    const mimeType = (file.type || "").toLowerCase();

    const fileName = (file.name || "").toLowerCase();

    return mimeType === "application/pdf" || fileName.endsWith(".pdf");
  };

  const handleFileSelect = (file) => {
    if (!file) {
      return;
    }

    if (!isPdfFile(file)) {
      showAlert(
        "error",

        t("alerts.pdfOnly", "Only PDF files are allowed."),
      );

      return;
    }

    clearAlert();

    setSelectedFile(file);
  };

  const handleInputChange = (event) => {
    const file = event.target.files?.[0];

    if (file) {
      handleFileSelect(file);
    }
  };

  /* =======================================================
     DRAG / DROP
     ======================================================= */

  const handleDrop = (event) => {
    event.preventDefault();

    event.stopPropagation();

    setDragActive(false);

    const file = event.dataTransfer.files?.[0];

    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();

    event.stopPropagation();

    setDragActive(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();

    event.stopPropagation();

    setDragActive(false);
  };

  /* =======================================================
     AMOUNT INPUT
     ======================================================= */

  const handleAmountChange = (event) => {
    setAmountInput(sanitizeAmountInput(event.target.value));
  };

  const handleEditAmountChange = (event) => {
    setEditAmountInput(sanitizeAmountInput(event.target.value));
  };

  /* =======================================================
     UPLOAD
     ======================================================= */

  const handleSubmit = async (event) => {
    event.preventDefault();

    clearAlert();

    if (!selectedFile || !renovationType || !amountInput) {
      showAlert(
        "error",

        t("alerts.missingFields"),
      );

      return;
    }

    if (!hasInvoiceContext || !isAuthenticated) {
      showAlert(
        "error",

        t(
          "alerts.noDossier",
          "No loan case or renovation context is available for invoice upload.",
        ),
      );

      return;
    }

    try {
      setIsUploading(true);

      const accessToken = await getAccessTokenSilently();

      await uploadRenovationInvoice({
        accessToken,
        loanCaseId,
        projectId,
        file: selectedFile,
        renovationType,
        amount: amountInput,
        notes,
      });

      showAlert(
        "success",

        t("alerts.uploadSuccess", "Invoice uploaded successfully."),
      );

      await refreshInvoices();

      resetForm();
    } catch (error) {
      showAlert(
        "error",

        error.message || t("alerts.uploadFailed", "Could not upload invoice."),
      );
    } finally {
      setIsUploading(false);
    }
  };

  /* =======================================================
     VIEW
     ======================================================= */

  const handleViewInvoice = async (invoice) => {
    try {
      clearAlert();

      const accessToken = await getAccessTokenSilently();

      const blob = await fetchRenovationInvoiceFile({
        accessToken,

        invoiceId: invoice.id,
      });

      const url = URL.createObjectURL(blob);

      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      setPreviewUrl(url);

      setPreviewInvoice(invoice);
    } catch (error) {
      showAlert(
        "error",

        error.message || t("preview.loadFailed", "Could not open invoice."),
      );
    }
  };

  /* =======================================================
     DOWNLOAD
     ======================================================= */

  const handleDownloadInvoice = async (invoice) => {
    try {
      clearAlert();

      const accessToken = await getAccessTokenSilently();

      const blob = await fetchRenovationInvoiceFile({
        accessToken,

        invoiceId: invoice.id,
      });

      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;

      link.download =
        invoice.latest_original_filename ||
        t("common.invoicePdf", "invoice.pdf");

      document.body.appendChild(link);

      link.click();

      link.remove();

      URL.revokeObjectURL(url);
    } catch (error) {
      showAlert(
        "error",

        error.message ||
          t("alerts.downloadFailed", "Could not download invoice."),
      );
    }
  };

  /* =======================================================
     DELETE
     ======================================================= */

  const handleStartDelete = (invoice) => {
    clearAlert();

    setDeleteInvoice(invoice);
  };

  const closeDeleteModal = () => {
    if (isDeletingInvoice) {
      return;
    }

    setDeleteInvoice(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteInvoice || !isAuthenticated) {
      return;
    }

    try {
      setIsDeletingInvoice(true);

      clearAlert();

      const accessToken = await getAccessTokenSilently();

      await deleteRenovationInvoice({
        accessToken,
        invoiceId: deleteInvoice.id,
      });

      if (previewInvoice?.id === deleteInvoice.id) {
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
        }

        setPreviewInvoice(null);

        setPreviewUrl("");
      }

      setDeleteInvoice(null);

      showAlert(
        "success",
        t("alerts.deleteSuccess", "Invoice deleted successfully."),
      );

      await refreshInvoices();
    } catch (error) {
      showAlert(
        "error",
        error.message || t("alerts.deleteFailed", "Could not delete invoice."),
      );
    } finally {
      setIsDeletingInvoice(false);
    }
  };

  /* =======================================================
     REPLACE
     ======================================================= */

  const handleStartReplace = (invoice) => {
    setEditInvoice(invoice);

    setEditRenovationType(invoice.renovation_type || "");

    setEditAmountInput(String(invoice.latest_amount ?? invoice.amount ?? ""));

    setEditNotes(invoice.latest_notes ?? "");

    setEditSelectedFile(null);

    if (replaceModalFileInputRef.current) {
      replaceModalFileInputRef.current.value = "";
    }
  };

  const handleEditFileSelect = (file) => {
    if (!file) {
      return;
    }

    if (!isPdfFile(file)) {
      showAlert(
        "error",

        t("alerts.pdfOnly", "Only PDF files are allowed."),
      );

      return;
    }

    clearAlert();

    setEditSelectedFile(file);
  };

  const handleSaveReplacement = async (event) => {
    event.preventDefault();

    if (!editInvoice) {
      return;
    }

    if (!editRenovationType || !editAmountInput) {
      showAlert(
        "error",

        t(
          "alerts.editMissingFields",
          "Please select a renovation type and enter the invoice amount.",
        ),
      );

      return;
    }

    if (!isAuthenticated) {
      return;
    }

    try {
      setIsSavingEdit(true);

      clearAlert();

      const accessToken = await getAccessTokenSilently();

      await replaceRenovationInvoice({
        accessToken,

        invoiceId: editInvoice.id,

        renovationType: editRenovationType,

        amount: editAmountInput,

        notes: editNotes,

        file: editSelectedFile,
      });

      showAlert(
        "success",

        t("alerts.replaceSuccess", "Invoice updated with a new version."),
      );

      closeEditModal();

      await refreshInvoices();
    } catch (error) {
      showAlert(
        "error",

        error.message || t("alerts.replaceFailed", "Could not update invoice."),
      );
    } finally {
      setIsSavingEdit(false);
    }
  };

  /* =======================================================
     RENDER
     ======================================================= */

  return (
    <div className="hb-invoices-page">
      {/* ===================================================
          TOAST
          =================================================== */}

      {alertState.visible && alertState.message ? (
        <div
          className={`hb-invoices-toast ${
            alertState.type === "error"
              ? "hb-invoices-toast--error"
              : "hb-invoices-toast--success"
          }`}
        >
          <div className="hb-invoices-toast__icon">
            {alertState.type === "error" ? (
              <AlertCircle size={18} />
            ) : (
              <CheckCircle2 size={18} />
            )}
          </div>

          <div className="hb-invoices-toast__content">
            <span>{alertState.message}</span>
          </div>

          <button
            type="button"
            className="hb-invoices-toast__close"
            onClick={clearAlert}
            aria-label={t("common.close", "Close")}
          >
            <X size={14} />
          </button>
        </div>
      ) : null}

      <div className="hb-invoices-page__shell">
        {/* =================================================
            HERO
            ================================================= */}

        <header className="hb-invoices-page__hero">
          <div>
            <p className="hb-invoices-page__eyebrow">{t("hero.eyebrow")}</p>

            <h1 className="hb-invoices-page__title">{t("hero.title")}</h1>

            <p className="hb-invoices-page__subtitle">{t("hero.subtitle")}</p>
          </div>

          <div className="hb-invoices-page__meta">
            <div className="hb-invoices-page__meta-card">
              <span className="hb-invoices-page__meta-label">
                {t("hero.operationLabel")}
              </span>

              <strong
                className={`hb-invoices-page__meta-value ${
                  operationCode === "—"
                    ? "hb-invoices-page__meta-value--empty"
                    : ""
                }`}
              >
                {operationCode}
              </strong>
            </div>

            <div className="hb-invoices-page__meta-card">
              <span className="hb-invoices-page__meta-label">
                {t("hero.lastUpdateLabel")}
              </span>

              <strong
                className={`hb-invoices-page__meta-value ${
                  lastUpdateLabel === "—"
                    ? "hb-invoices-page__meta-value--empty"
                    : ""
                }`}
              >
                {lastUpdateLabel}
              </strong>
            </div>
          </div>
        </header>

        {/* =================================================
            GUIDANCE
            ================================================= */}

        <div className="hb-invoices-guidance">
          <h3 className="hb-invoices-guidance__title">
            {t("guidance.title", "Upload and track your renovation invoices")}
          </h3>

          <div className="hb-invoices-guidance__grid">
            <div className="hb-invoices-guidance__item hb-invoices-guidance__item--new">
              <strong>{t("guidance.newTitle", "Add a new invoice")}</strong>

              <p>
                {t(
                  "guidance.newText",
                  "Upload invoices issued for renovation works. The invoices will be visible to your One-Stop Shop and lender for project monitoring.",
                )}
              </p>
            </div>

            <div className="hb-invoices-guidance__item hb-invoices-guidance__item--replace">
              <strong>
                {t("guidance.replaceTitle", "Correct an existing invoice")}
              </strong>

              <p>
                {t(
                  "guidance.replaceText",
                  "If an uploaded invoice needs to be corrected, upload a revised version. Previous versions remain available in the invoice history.",
                )}
              </p>
            </div>
          </div>
        </div>

        {/* =================================================
            CONTENT
            ================================================= */}

        <div className="hb-invoices-page__content">
          {/* ===============================================
              UPLOAD
              =============================================== */}

          <section className="hb-invoices-card hb-invoices-card--upload">
            <div className="hb-invoices-card__top">
              <div>
                <h2 className="hb-invoices-card__title">
                  {t("uploadCard.title", "Upload renovation invoice")}
                </h2>

                <p className="hb-invoices-card__text">
                  {t(
                    "uploadCard.subtitle",
                    "Add an invoice related to your renovation project.",
                  )}

                  <span className="hb-invoices-card__hint">
                    {" "}
                    {t(
                      "uploadCard.hint",
                      "Invoices are recorded as renovation expenditure and are visible to your lender and One-Stop Shop.",
                    )}
                  </span>
                </p>
              </div>

              <span className="hb-invoices-card__visibility-pill">
                {t("uploadCard.sharedPill", "Visible to lender and OSS")}
              </span>
            </div>

            <form className="hb-invoices-form" onSubmit={handleSubmit}>
              {/* ===========================================
                  DROPZONE
                  =========================================== */}

              <div
                className={`hb-invoices-dropzone ${
                  dragActive ? "is-dragging" : ""
                } ${selectedFile ? "has-file" : ""}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  className="hb-invoices-dropzone__input"
                  onChange={handleInputChange}
                />

                <div className="hb-invoices-dropzone__icon-wrap">
                  <Upload size={18} />
                </div>

                {!selectedFile ? (
                  <>
                    <h3 className="hb-invoices-dropzone__title">
                      {t("dropzone.title")}
                    </h3>

                    <p className="hb-invoices-dropzone__text">
                      {t("dropzone.formats")}
                    </p>

                    <button
                      type="button"
                      className="hb-invoices-dropzone__button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                    >
                      {t("buttons.chooseFile")}
                    </button>
                  </>
                ) : (
                  <div className="hb-invoices-dropzone__selected">
                    <FileText size={16} />

                    <div>
                      <strong>{selectedFile.name}</strong>

                      <p>{t("dropzone.readyToUpload")}</p>
                    </div>

                    <button
                      type="button"
                      className="hb-invoices-dropzone__change"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                    >
                      {t("buttons.change")}
                    </button>
                  </div>
                )}
              </div>

              {/* ===========================================
                  TYPE / AMOUNT
                  =========================================== */}

              <div className="hb-invoices-form__grid">
                <label className="hb-invoices-field hb-invoices-field--with-tooltip">
                  <span className="hb-invoices-field__label">
                    <Tag size={13} />

                    {t("fields.renovationType")}

                    <span className="hb-invoices-tooltip-wrap" tabIndex={0}>
                      <Info
                        size={14}
                        strokeWidth={2.2}
                        className="hb-invoices-tooltip-icon"
                      />
                    </span>
                  </span>

                  <div className="hb-invoices-select-wrap">
                    <select
                      value={renovationType}
                      onChange={(event) =>
                        setRenovationType(event.target.value)
                      }
                      disabled={isUploading}
                    >
                      <option value="">
                        {t("fields.selectRenovationType")}
                      </option>

                      {RENOVATION_TYPE_KEYS.map((typeKey) => (
                        <option key={typeKey} value={typeKey}>
                          {t(`renovationTypes.${typeKey}`)}
                        </option>
                      ))}
                    </select>

                    <ChevronDown
                      size={18}
                      className="hb-invoices-select-wrap__icon"
                    />
                  </div>

                  <span className="hb-invoices-tooltip-bubble">
                    {t("tooltips.renovationType")}
                  </span>
                </label>

                <label className="hb-invoices-field hb-invoices-field--with-tooltip">
                  <span className="hb-invoices-field__label">
                    {t("fields.invoiceAmount")}

                    <span className="hb-invoices-tooltip-wrap" tabIndex={0}>
                      <Info
                        size={14}
                        strokeWidth={2.2}
                        className="hb-invoices-tooltip-icon"
                      />
                    </span>
                  </span>

                  <div className="hb-invoices-amount">
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder={t("fields.invoiceAmountPlaceholder")}
                      value={formatCurrencyDisplay(amountInput)}
                      onChange={handleAmountChange}
                      disabled={isUploading}
                    />

                    <span className="hb-invoices-amount__suffix">€</span>
                  </div>

                  <span className="hb-invoices-tooltip-bubble">
                    {t("tooltips.invoiceAmount")}
                  </span>
                </label>
              </div>

              {/* ===========================================
                  NOTES
                  =========================================== */}

              <label className="hb-invoices-field hb-invoices-field--with-tooltip">
                <span className="hb-invoices-field__label">
                  {t("fields.notesOptional")}

                  <span className="hb-invoices-tooltip-wrap" tabIndex={0}>
                    <Info
                      size={14}
                      strokeWidth={2.2}
                      className="hb-invoices-tooltip-icon"
                    />
                  </span>
                </span>

                <textarea
                  rows="2"
                  placeholder={t("fields.notesPlaceholder")}
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  disabled={isUploading}
                />

                <span className="hb-invoices-tooltip-bubble">
                  {t("tooltips.notes")}
                </span>
              </label>

              {/* ===========================================
                  FOOTER
                  =========================================== */}

              <div className="hb-invoices-form__footer">
                <p className="hb-invoices-form__hint">
                  {t(
                    "uploadCard.footerHint",
                    "The invoice amount contributes to the total invoiced renovation expenditure.",
                  )}
                </p>

                <div className="hb-invoices-form__actions">
                  <button
                    type="button"
                    className="hb-invoices-btn hb-invoices-btn--ghost"
                    onClick={resetForm}
                    disabled={isUploading}
                  >
                    {t("buttons.clear")}
                  </button>

                  <button
                    type="submit"
                    className="hb-invoices-btn hb-invoices-btn--primary"
                    disabled={isUploading}
                  >
                    <Plus size={14} />

                    {isUploading
                      ? t("buttons.uploading", "Uploading...")
                      : t("buttons.uploadInvoice")}
                  </button>
                </div>
              </div>
            </form>
          </section>

          {/* ===============================================
              INVOICE TABLE
              =============================================== */}

          <section className="hb-invoices-card hb-invoices-card--table">
            <div className="hb-invoices-card__top hb-invoices-card__top--summary">
              <div>
                <h2 className="hb-invoices-card__title">
                  {t("tableCard.title")}
                </h2>

                <p className="hb-invoices-card__text">
                  {t("tableCard.subtitle")}
                </p>
              </div>

              {/* ===========================================
                  RENOVATION EXPENDITURE SUMMARY
                  =========================================== */}

              <div className="hb-invoices-summary">
                <div className="hb-invoices-summary__item">
                  <span>{t("summary.totalInvoices", "Invoices uploaded")}</span>

                  <strong>{effectiveInvoices.length}</strong>
                </div>

                <div className="hb-invoices-summary__item">
                  <span>{t("summary.totalAmount", "Total invoiced")}</span>

                  <strong>{formatEuro(totalAmount)}</strong>
                </div>

                {safeInitialRenovationEstimate > 0 ? (
                  <div className="hb-invoices-summary__item">
                    <span>
                      {t(
                        "summary.initialRenovationEstimate",
                        "Initial renovation estimate",
                      )}
                    </span>

                    <strong>{formatEuro(safeInitialRenovationEstimate)}</strong>
                  </div>
                ) : null}

                {invoiceDocumentationTarget > 0 ? (
                  <div className="hb-invoices-summary__item hb-invoices-summary__item--remaining">
                    <span>
                      {t(
                        "summary.remainingToDocument",
                        "Remaining to document",
                      )}
                    </span>

                    <strong>
                      {formatEuro(remainingExpenditureToDocument)}
                    </strong>
                  </div>
                ) : null}
              </div>
              {safeLenderRenovationFinancing > 0 ||
              additionalFundingSources.length > 0 ? (
                <div className="hb-invoices-funding-summary">
                  {safeLenderRenovationFinancing > 0 ? (
                    <div className="hb-invoices-funding-summary__item">
                      <div className="hb-invoices-funding-summary__heading">
                        <span>
                          {t("summary.lenderFinancing", "Lender financing")}
                        </span>

                        <small className="hb-invoices-funding-status hb-invoices-funding-status--approved">
                          {t("summary.statusApproved", "Approved")}
                        </small>
                      </div>

                      <strong>
                        {formatEuro(safeLenderRenovationFinancing)}
                      </strong>
                    </div>
                  ) : null}

                  {additionalFundingSources.map((source, index) => {
                    const isExpected = source.status === "EXPECTED";

                    const isApproved = [
                      "APPROVED",
                      "CONFIRMED",
                      "ACTIVE",
                    ].includes(source.status);

                    let fallbackLabel = "Other funding";

                    if (source.sourceType === "SOFT_LOAN") {
                      fallbackLabel = "Soft loan";
                    } else if (
                      source.sourceType === "SUBSIDY" ||
                      source.sourceType === "GRANT"
                    ) {
                      fallbackLabel = "Subsidy / grant";
                    } else if (source.sourceType === "OWN_FUNDS") {
                      fallbackLabel = "Own funds";
                    }

                    return (
                      <div
                        key={source.id || `${source.sourceType}-${index}`}
                        className="hb-invoices-funding-summary__item"
                      >
                        <div className="hb-invoices-funding-summary__heading">
                          <span>
                            {source.name ||
                              t(
                                `summary.fundingTypes.${source.sourceType}`,
                                fallbackLabel,
                              )}
                          </span>

                          <small
                            className={`hb-invoices-funding-status ${
                              isExpected
                                ? "hb-invoices-funding-status--expected"
                                : isApproved
                                  ? "hb-invoices-funding-status--approved"
                                  : ""
                            }`}
                          >
                            {isExpected
                              ? t("summary.statusExpected", "Expected")
                              : isApproved
                                ? t("summary.statusApproved", "Approved")
                                : t(
                                    `summary.statuses.${source.status}`,
                                    source.status,
                                  )}
                          </small>
                        </div>

                        <strong>{formatEuro(source.amount)}</strong>
                      </div>
                    );
                  })}

                  <div className="hb-invoices-funding-summary__item hb-invoices-funding-summary__item--total">
                    <span>
                      {t("summary.confirmedFunding", "Confirmed funding")}
                    </span>

                    <strong>{formatEuro(confirmedFundingTotal)}</strong>
                  </div>

                  {expectedFundingTotal > 0 ? (
                    <div className="hb-invoices-funding-summary__item hb-invoices-funding-summary__item--expected-total">
                      <span>
                        {t(
                          "summary.expectedFunding",
                          "Expected additional funding",
                        )}
                      </span>

                      <strong>{formatEuro(expectedFundingTotal)}</strong>
                    </div>
                  ) : null}

                  <div className="hb-invoices-funding-summary__item hb-invoices-funding-summary__item--potential">
                    <span>
                      {t("summary.potentialFunding", "Potential total funding")}
                    </span>

                    <strong>{formatEuro(potentialFundingTotal)}</strong>
                  </div>

                  {potentialFundingBalance !== null ? (
                    <div className="hb-invoices-funding-summary__item">
                      <span>
                        {t(
                          "summary.potentialFundingBalance",
                          "Potential funding balance",
                        )}
                      </span>

                      <strong>
                        {potentialFundingBalance >= 0
                          ? `+${formatEuro(potentialFundingBalance)}`
                          : formatEuro(potentialFundingBalance)}
                      </strong>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            {/* =============================================
                INVOICE PROGRESS
                ============================================= */}

            {invoiceDocumentationTarget > 0 ? (
              <div className="hb-invoices-financing-progress">
                <div className="hb-invoices-financing-progress__header">
                  <div>
                    <span>
                      {t(
                        "summary.expenditureProgress",
                        "Invoice documentation progress",
                      )}
                    </span>

                    <strong>
                      {formatEuro(totalAmount)}
                      {" / "}
                      {formatEuro(invoiceDocumentationTarget)}
                    </strong>
                  </div>

                  <span className="hb-invoices-financing-progress__percentage">
                    {rawInvoicedProgress.toFixed(1)}%
                  </span>
                </div>

                <div className="hb-invoices-financing-progress__track">
                  <div
                    className="hb-invoices-financing-progress__fill"
                    style={{
                      width: `${invoicedProgress}%`,
                    }}
                  />
                </div>

                <div className="hb-invoices-financing-progress__footer">
                  <span>
                    {t("summary.documented", "Documented")}

                    <strong>{formatEuro(totalAmount)}</strong>
                  </span>

                  <span>
                    {t("summary.remainingExpenditure", "Remaining to document")}

                    <strong>
                      {formatEuro(remainingExpenditureToDocument)}
                    </strong>
                  </span>
                </div>

                <p className="hb-invoices-financing-progress__note">
                  {t(
                    "summary.progressNote",
                    "This progress tracks invoices against the renovation budget. Approved and expected financing sources are shown separately above.",
                  )}
                </p>
              </div>
            ) : null}
            {invoicesAboveTarget > 0 ? (
              <p className="hb-invoices-financing-progress__warning">
                {t(
                  "summary.overBudget",
                  "Documented expenditure exceeds the current renovation budget by {{amount}}.",
                  {
                    amount: formatEuro(invoicesAboveTarget),
                  },
                )}
              </p>
            ) : null}

            {/* =============================================
                TABLE
                ============================================= */}

            <div className="hb-invoices-table-wrap">
              <table className="hb-invoices-table">
                <colgroup>
                  <col className="hb-invoices-table__col-file" />
                  <col className="hb-invoices-table__col-type" />
                  <col className="hb-invoices-table__col-amount" />
                  <col className="hb-invoices-table__col-uploaded" />
                  <col className="hb-invoices-table__col-version" />
                  <col className="hb-invoices-table__col-actions" />
                </colgroup>

                <thead>
                  <tr>
                    <th>{t("table.file", "Invoice")}</th>

                    <th>{t("table.renovationType", "Renovation type")}</th>

                    <th>{t("table.amount", "Amount")}</th>

                    <th>{t("table.uploaded", "Uploaded")}</th>

                    <th>{t("table.version", "Version")}</th>

                    <th className="hb-invoices-table__actions-col">
                      {t("table.actions", "Actions")}
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {effectiveInvoices.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="hb-invoices-table__empty">
                        <strong>
                          {t("table.empty", "No invoices uploaded yet")}
                        </strong>

                        <div className="hb-invoices-empty-hint">
                          {t(
                            "table.emptyHint",
                            "Upload the first invoice for this renovation project using the form above.",
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    effectiveInvoices.map((invoice) => (
                      <tr key={invoice.id}>
                        {/* FILE */}

                        <td>
                          <div className="hb-invoices-file">
                            <FileText size={16} />

                            <div>
                              <strong>
                                {invoice.latest_original_filename ||
                                  invoice.file_name ||
                                  t("common.invoicePdf", "Invoice.pdf")}

                                {(invoice.latest_version ?? 1) > 1 ? (
                                  <span className="hb-invoices-badge">
                                    {t("common.updated", "Updated")}
                                  </span>
                                ) : null}
                              </strong>

                              <span>
                                {t(
                                  "table.invoiceDocument",
                                  "Renovation invoice",
                                )}
                              </span>
                            </div>
                          </div>
                        </td>

                        {/* TYPE */}

                        <td>
                          <span className="hb-invoices-table__type">
                            {t(
                              `renovationTypes.${invoice.renovation_type}`,
                              invoice.renovation_type || "—",
                            )}
                          </span>
                        </td>

                        {/* AMOUNT */}

                        <td>
                          <strong className="hb-invoices-table__amount">
                            {formatEuro(
                              invoice.latest_amount ?? invoice.amount ?? 0,
                            )}
                          </strong>
                        </td>

                        {/* UPLOADED */}

                        <td>
                          <div className="hb-invoices-uploaded">
                            <span className="hb-invoices-uploaded__line">
                              <CalendarDays size={13} />

                              {invoice.uploaded_date || "—"}
                            </span>

                            <span className="hb-invoices-uploaded__line">
                              <Clock3 size={13} />

                              {invoice.uploaded_time || "—"}
                            </span>
                          </div>
                        </td>

                        {/* VERSION */}

                        <td>
                          <span className="hb-invoices-table__version">
                            {formatVersionLabel(
                              invoice.latest_version ?? invoice.version,

                              t,
                            )}
                          </span>
                        </td>

                        {/* ACTIONS */}

                        <td>
                          <div className="hb-invoices-actions">
                            <div className="hb-invoices-actions__hint">
                              {(invoice.latest_version ?? 1) > 1
                                ? t(
                                    "table.replaceHintUpdated",
                                    "Previous versions are kept in the invoice history.",
                                  )
                                : t(
                                    "table.replaceHint",
                                    "Update this invoice only when replacing it with a revised version.",
                                  )}
                            </div>

                            <div className="hb-invoices-actions__buttons">
                              <button
                                type="button"
                                className="hb-invoices-icon-btn"
                                title={t("actions.view", "View invoice")}
                                onClick={() => handleViewInvoice(invoice)}
                              >
                                <Eye size={15} />
                              </button>

                              <button
                                type="button"
                                className="hb-invoices-icon-btn"
                                title={t(
                                  "actions.download",
                                  "Download invoice",
                                )}
                                onClick={() => handleDownloadInvoice(invoice)}
                              >
                                <Download size={15} />
                              </button>

                              <button
                                type="button"
                                className="hb-invoices-icon-btn"
                                title={t(
                                  "actions.updateVersion",
                                  "Upload revised version",
                                )}
                                onClick={() => handleStartReplace(invoice)}
                              >
                                <Pencil size={15} />
                              </button>

                              <button
                                type="button"
                                className="hb-invoices-icon-btn is-danger"
                                title={t("actions.delete", "Delete invoice")}
                                onClick={() => handleStartDelete(invoice)}
                              >
                                <Trash2 size={15} />
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>

      {/* ===================================================
          DELETE MODAL
          =================================================== */}

      {deleteInvoice ? (
        <div className="hb-invoices-modal-overlay" onClick={closeDeleteModal}>
          <div
            className="hb-invoices-modal hb-invoices-modal--delete"
            role="dialog"
            aria-modal="true"
            aria-labelledby="hb-invoices-delete-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="hb-invoices-delete-modal">
              <div className="hb-invoices-delete-modal__icon">
                <Trash2 size={22} />
              </div>

              <div className="hb-invoices-delete-modal__content">
                <span className="hb-invoices-delete-modal__eyebrow">
                  {t("deleteModal.eyebrow", "Delete invoice")}
                </span>

                <h3
                  id="hb-invoices-delete-title"
                  className="hb-invoices-delete-modal__title"
                >
                  {t("deleteModal.title", "Do you want to delete this file?")}
                </h3>

                <p className="hb-invoices-delete-modal__text">
                  {t(
                    "deleteModal.description",
                    "This will permanently delete the invoice and its stored file. This action cannot be undone.",
                  )}
                </p>

                <div className="hb-invoices-delete-modal__file">
                  <FileText size={17} />

                  <div>
                    <span>{t("deleteModal.fileLabel", "File to delete")}</span>

                    <strong>
                      {deleteInvoice.latest_original_filename ||
                        deleteInvoice.file_name ||
                        t("common.invoicePdf", "Invoice.pdf")}
                    </strong>
                  </div>
                </div>
              </div>

              <div className="hb-invoices-delete-modal__actions">
                <button
                  type="button"
                  className="hb-invoices-btn hb-invoices-btn--ghost"
                  onClick={closeDeleteModal}
                  disabled={isDeletingInvoice}
                >
                  {t("common.cancel", "Cancel")}
                </button>

                <button
                  type="button"
                  className="hb-invoices-btn hb-invoices-btn--danger"
                  onClick={handleConfirmDelete}
                  disabled={isDeletingInvoice}
                >
                  <Trash2 size={14} />

                  {isDeletingInvoice
                    ? t("deleteModal.deleting", "Deleting...")
                    : t("deleteModal.confirm", "Delete file")}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* ===================================================
          PREVIEW MODAL
          =================================================== */}

      {previewInvoice ? (
        <div
          className="hb-invoices-modal-overlay"
          onClick={() => {
            if (previewUrl) {
              URL.revokeObjectURL(previewUrl);
            }

            setPreviewUrl("");

            setPreviewInvoice(null);
          }}
        >
          <div
            className="hb-invoices-modal hb-invoices-modal--preview"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="hb-invoices-modal__header">
              <h3>
                {previewInvoice.latest_original_filename ||
                  t("preview.title", "Invoice preview")}
              </h3>

              <button
                type="button"
                className="hb-invoices-modal__close"
                onClick={() => {
                  if (previewUrl) {
                    URL.revokeObjectURL(previewUrl);
                  }

                  setPreviewUrl("");

                  setPreviewInvoice(null);
                }}
              >
                <X size={16} />
              </button>
            </div>

            <div className="hb-invoices-modal__body">
              {previewUrl ? (
                <iframe
                  title={t("preview.iframeTitle", "Invoice preview")}
                  src={previewUrl}
                  className="hb-invoices-modal__iframe"
                />
              ) : (
                <p>
                  {t(
                    "preview.unavailable",
                    "Preview will be available when file access is connected.",
                  )}
                </p>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {/* ===================================================
          EDIT MODAL
          =================================================== */}

      {editInvoice ? (
        <div className="hb-invoices-modal-overlay" onClick={closeEditModal}>
          <div
            className="hb-invoices-modal hb-invoices-modal--edit"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="hb-invoices-modal__header">
              <div className="hb-invoices-modal__header-content">
                <h3>{t("editModal.title", "Update invoice")}</h3>

                <p>
                  {t(
                    "editModal.subtitle",
                    "Replace the existing invoice with a corrected version. Use this only for corrections, not for new contractor invoices.",
                  )}
                </p>
              </div>

              <button
                type="button"
                className="hb-invoices-modal__close"
                onClick={closeEditModal}
              >
                <X size={16} />
              </button>
            </div>

            <div className="hb-invoices-modal__body hb-invoices-modal__body--edit">
              <form
                className="hb-invoices-edit-form"
                onSubmit={handleSaveReplacement}
              >
                <div className="hb-invoices-edit-form__current-file">
                  <FileText size={16} />

                  <div>
                    <strong>
                      {editInvoice.latest_original_filename ||
                        t("editModal.currentInvoice", "Current invoice")}
                    </strong>

                    <span>
                      {t("editModal.currentVersion", "Current version")}
                      {": "}

                      {formatVersionLabel(
                        editInvoice.latest_version ?? editInvoice.version,

                        t,
                      )}
                    </span>
                  </div>
                </div>

                <div className="hb-invoices-form__grid">
                  <label className="hb-invoices-field">
                    <span className="hb-invoices-field__label">
                      <Tag size={13} />

                      {t("fields.renovationType")}
                    </span>

                    <div className="hb-invoices-select-wrap">
                      <select
                        value={editRenovationType}
                        onChange={(event) =>
                          setEditRenovationType(event.target.value)
                        }
                        disabled={isSavingEdit}
                      >
                        <option value="">
                          {t("fields.selectRenovationType")}
                        </option>

                        {RENOVATION_TYPE_KEYS.map((typeKey) => (
                          <option key={typeKey} value={typeKey}>
                            {t(`renovationTypes.${typeKey}`)}
                          </option>
                        ))}
                      </select>

                      <ChevronDown
                        size={18}
                        className="hb-invoices-select-wrap__icon"
                      />
                    </div>
                  </label>

                  <label className="hb-invoices-field">
                    <span className="hb-invoices-field__label">
                      {t("fields.invoiceAmount")}
                    </span>

                    <div className="hb-invoices-amount">
                      <input
                        type="text"
                        inputMode="decimal"
                        placeholder={t("fields.invoiceAmountPlaceholder")}
                        value={formatCurrencyDisplay(editAmountInput)}
                        onChange={handleEditAmountChange}
                        disabled={isSavingEdit}
                      />

                      <span className="hb-invoices-amount__suffix">€</span>
                    </div>
                  </label>
                </div>

                <label className="hb-invoices-field">
                  <span className="hb-invoices-field__label">
                    {t("fields.notesOptional")}
                  </span>

                  <textarea
                    rows="3"
                    placeholder={t("fields.notesPlaceholder")}
                    value={editNotes}
                    onChange={(event) => setEditNotes(event.target.value)}
                    disabled={isSavingEdit}
                  />
                </label>

                <div className="hb-invoices-edit-form__upload">
                  <div className="hb-invoices-edit-form__upload-header">
                    <span className="hb-invoices-field__label">
                      {t("editModal.replacePdf", "Replace PDF file")}
                    </span>

                    <button
                      type="button"
                      className="hb-invoices-dropzone__button"
                      onClick={() => replaceModalFileInputRef.current?.click()}
                      disabled={isSavingEdit}
                    >
                      {t("editModal.choosePdf", "Choose revised PDF")}
                    </button>

                    <input
                      ref={replaceModalFileInputRef}
                      type="file"
                      accept=".pdf,application/pdf"
                      className="hb-invoices-dropzone__input"
                      onChange={(event) =>
                        handleEditFileSelect(event.target.files?.[0])
                      }
                    />
                  </div>

                  <div className="hb-invoices-edit-form__upload-info">
                    {editSelectedFile ? (
                      <div className="hb-invoices-edit-form__selected-file">
                        <FileText size={14} />

                        <span>{editSelectedFile.name}</span>
                      </div>
                    ) : (
                      <div className="hb-invoices-edit-form__selected-file is-empty">
                        <Info size={14} />

                        <span>
                          {t(
                            "editModal.noPdfSelected",
                            "No new PDF selected yet. Choose a revised file for the next version.",
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* =========================================
                    VERSION HISTORY
                    ========================================= */}

                <div className="hb-invoices-version-history">
                  <div className="hb-invoices-version-history__title">
                    <History size={15} />

                    <span>
                      {t("editModal.versionHistory", "Version history")}
                    </span>
                  </div>

                  <div className="hb-invoices-version-history__list">
                    {(editInvoice.versions && editInvoice.versions.length > 0
                      ? [...editInvoice.versions].sort(
                          (a, b) =>
                            Number(b.version_number) - Number(a.version_number),
                        )
                      : [
                          {
                            id: `${editInvoice.id}-fallback`,

                            version_number:
                              editInvoice.latest_version ??
                              editInvoice.version ??
                              1,

                            original_filename:
                              editInvoice.latest_original_filename ||
                              editInvoice.file_name,

                            amount:
                              editInvoice.latest_amount ?? editInvoice.amount,

                            notes: editInvoice.latest_notes ?? "",

                            uploaded_date: editInvoice.uploaded_date,

                            uploaded_time: editInvoice.uploaded_time,
                          },
                        ]
                    ).map((versionItem) => (
                      <div
                        key={versionItem.id || versionItem.version_number}
                        className="hb-invoices-version-history__item"
                      >
                        <div className="hb-invoices-version-history__item-top">
                          <strong>
                            {formatVersionLabel(versionItem.version_number, t)}
                          </strong>

                          <span>
                            {versionItem.uploaded_date ||
                              (versionItem.uploaded_at
                                ? new Date(
                                    versionItem.uploaded_at,
                                  ).toLocaleDateString("en-GB")
                                : "—")}

                            {" · "}

                            {versionItem.uploaded_time ||
                              (versionItem.uploaded_at
                                ? new Date(
                                    versionItem.uploaded_at,
                                  ).toLocaleTimeString("en-GB", {
                                    hour: "2-digit",

                                    minute: "2-digit",
                                  })
                                : "—")}
                          </span>
                        </div>

                        <div className="hb-invoices-version-history__item-body">
                          <span>{versionItem.original_filename || "—"}</span>

                          <span>{formatEuro(versionItem.amount || 0)}</span>
                        </div>

                        {versionItem.notes ? (
                          <p className="hb-invoices-version-history__notes">
                            {versionItem.notes}
                          </p>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>

                {/* =========================================
                    EDIT ACTIONS
                    ========================================= */}

                <div className="hb-invoices-edit-form__actions">
                  <button
                    type="button"
                    className="hb-invoices-btn hb-invoices-btn--ghost"
                    onClick={closeEditModal}
                    disabled={isSavingEdit}
                  >
                    {t("common.cancel", "Cancel")}
                  </button>

                  <button
                    type="submit"
                    className="hb-invoices-btn hb-invoices-btn--primary"
                    disabled={isSavingEdit}
                  >
                    <Pencil size={14} />

                    {isSavingEdit
                      ? t("common.saving", "Saving...")
                      : t("editModal.saveVersion", "Save new version")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
